import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useMemo, memo, useEffect, useReducer, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Text, Advice, createUseStyles } from 'hoi-poi-ui';
import { useWeb5Context } from "../../../lib/Web5Context";
import { getFieldValidation, createFieldValidation, updateFieldValidation } from "../../../lib/services/fieldsValidations";
import { getValidationsTypesList } from "../../../lib/literals";
import { showErrorToast } from "../../../lib/errors";
import { successToast } from "../../../utils/toast";
import ForceForm, { validate } from "../../../components/ForceForm";
import HyperDrawer from "../../../components/HyperDrawer";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'FormFieldsValidations');
const initialState = {
  data: {},
  originalData: {},
  fields: [],
  crudType: null,
  errors: {},
  isOpen: false,
  isLoading: true,
  isSaving: false,
  isReadOnly: null
};
function reducer(state, action) {
  switch (action.type) {
    case 'init':
      return _objectSpread(_objectSpread({}, state), {}, {
        originalData: action.data,
        data: action.data,
        isReadOnly: action.isReadOnly,
        isDuplicate: action.isDuplicate,
        errors: {},
        isOpen: true,
        isLoading: false
      });
    case 'close':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: false,
        isOpen: false
      });
    case 'changeData':
      return _objectSpread(_objectSpread({}, state), {}, {
        originalData: _objectSpread({}, state.originalData),
        data: action.data
      });
    case 'setErrors':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: false,
        errors: action.errors
      });
    case 'setSaving':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: action.value
      });
    default:
      throw new Error('No action provided');
  }
}
const FormFieldsValidations = _ref => {
  var _permissions$fieldsVa2, _state$data$type2, _state$data2, _state$data3;
  let {
    onRef,
    entityLogName,
    overrides,
    refreshList = () => {},
    onSave = () => {},
    onStack = () => {},
    setActiveRowId = () => {},
    stackIndex = 0
  } = _ref;
  const {
    t,
    permissions,
    logEvent
  } = useWeb5Context({
    overrides
  });
  const [state, dispatch] = useReducer(reducer, initialState);
  const schema = useRef();
  const classes = useStyles();

  // Child method
  useEffect(() => {
    onRef && onRef({
      open(_ref2) {
        let {
          id,
          isDuplicate,
          type
        } = _ref2;
        const fieldTypesList = getValidationsTypesList(t);
        const fieldTypesObj = fieldTypesList.reduce((obj, el) => _objectSpread(_objectSpread({}, obj), {}, {
          [el.value]: el.label
        }), {});
        const getFieldValidationData = id => id ? getFieldValidation(id) : Promise.resolve({
          isStandard: false,
          type,
          code: `var handler = function (value) {
  return '';
}`
        });
        onStack(stackIndex + 2);
        getFieldValidationData(id).then(data => {
          var _permissions$fieldsVa;
          const isReadOnly = data.isStandard && !isDuplicate || !(permissions !== null && permissions !== void 0 && (_permissions$fieldsVa = permissions.fieldsValidations) !== null && _permissions$fieldsVa !== void 0 && _permissions$fieldsVa.update);
          schema.current = {
            required: ['name', 'description', 'type'],
            properties: {
              name: {
                maxLength: 50,
                type: ['string']
              },
              type: {
                type: ['string']
              },
              decription: {
                maxLength: 200,
                type: ['string']
              },
              code: {
                type: ['string']
              }
            },
            layout: {
              id: 'validations',
              uiGrid: 'linearVertical',
              composition: [{
                schemaId: 'name',
                uiType: 'text',
                label: 'cfm_label_name',
                placeholder: 'placeholder_text_field',
                isReadOnly
              }, {
                schemaId: 'description',
                uiType: 'textarea',
                label: 'cfm_label_description',
                placeholder: 'placeholder_text_field',
                isReadOnly
              }, {
                schemaId: 'type',
                uiType: 'list',
                label: 'cfm_label_data_type',
                placeholder: 'label_selectone',
                options: fieldTypesList,
                isReadOnly: isReadOnly || !!type
              }, {
                schemaId: 'code',
                uiType: 'code',
                label: 'cfm_label_code',
                isReadOnly
              }]
            }
          };
          data.type = data.type ? {
            value: data.type,
            label: fieldTypesObj[data.type]
          } : null;
          if (isDuplicate) {
            data.id = null;
            data.name = '';
            data.description = '';
          }
          dispatch({
            type: 'init',
            originalData: data,
            data,
            getValidationsTypesList,
            isReadOnly,
            isDuplicate
          });
        }).catch(error => {
          console.error(error);
          dispatch({
            type: 'setErrors',
            errors: error
          });
          showErrorToast(t, error);
        });
      }
    });
  }, [onStack, onRef, t, stackIndex, permissions === null || permissions === void 0 ? void 0 : (_permissions$fieldsVa2 = permissions.fieldsValidations) === null || _permissions$fieldsVa2 === void 0 ? void 0 : _permissions$fieldsVa2.update]);
  const handleOnSave = useCallback(() => {
    var _state$data$type;
    logEvent({
      event: entityLogName,
      functionality: state.data.id ? 'update' : 'create'
    });
    if (state !== null && state !== void 0 && state.isReadOnly) {
      setActiveRowId(null);
      return dispatch({
        type: 'close'
      });
    }
    const finalData = {
      id: state.data.id,
      name: state.data.name,
      description: state.data.description,
      type: ((_state$data$type = state.data.type) === null || _state$data$type === void 0 ? void 0 : _state$data$type.value) || null,
      code: state.data.code
    };
    validate(finalData, schema.current).then(_ref3 => {
      let {
        isValid,
        errors,
        cleanData
      } = _ref3;
      if (!isValid) {
        dispatch({
          type: 'setErrors',
          errors
        });
      } else {
        dispatch({
          type: 'setSaving',
          value: true
        });
        const file = cleanData.code;
        const saveValidation = (id, data, file) => id ? updateFieldValidation(id, data, file) : createFieldValidation(data, file);
        delete cleanData.code;
        saveValidation(state.data.id, cleanData, file).then(data => {
          refreshList && refreshList();
          setActiveRowId(null);
          dispatch({
            type: 'close'
          });
          successToast({
            t,
            text: state.data.id ? t('cfm_success_update_validation') : t('cfm_success_create_validation')
          });
          onSave(data);
          onStack(stackIndex - 2);
        }).catch(error => {
          console.error(error);
          showErrorToast(t, error);
          dispatch({
            type: 'setErrors',
            errors: error
          });
        });
      }
    });
  }, [logEvent, entityLogName, state.data.id, state.data.name, state.data.description, (_state$data$type2 = state.data.type) === null || _state$data$type2 === void 0 ? void 0 : _state$data$type2.value, state.data.code, state === null || state === void 0 ? void 0 : state.isReadOnly, setActiveRowId, refreshList, t, onSave, onStack, stackIndex]);
  const locales = useMemo(() => {
    if (state !== null && state !== void 0 && state.isReadOnly) {
      var _state$data;
      return {
        title: (state === null || state === void 0 ? void 0 : (_state$data = state.data) === null || _state$data === void 0 ? void 0 : _state$data.name) || '',
        cancel: null,
        save: t('close')
      };
    } else {
      return {
        title: state.data.id ? t('cfm_title_update_validation') : t('cfm_title_create_validation'),
        cancel: t('cfm_action_cancel'),
        save: t('cfm_action_save')
      };
    }
  }, [state.data, state === null || state === void 0 ? void 0 : state.isReadOnly, t]);
  const handleOnCancel = useCallback(() => {
    setActiveRowId(null);
    dispatch({
      type: 'close'
    });
    onStack(stackIndex - 2);
  }, [setActiveRowId, onStack, stackIndex]);
  const onChange = useCallback((data, field) => {
    if (field === 'name') {
      var _data$name;
      const name = (_data$name = data.name) === null || _data$name === void 0 ? void 0 : _data$name.replace(/\\|\//g, '');
      let finalData = _objectSpread(_objectSpread({}, data), {}, {
        name
      });
      return dispatch({
        type: 'changeData',
        data: finalData
      });
    }
    return dispatch({
      type: 'changeData',
      data
    });
  }, []);
  return /*#__PURE__*/React.createElement(HyperDrawer, {
    isOpen: state.isOpen,
    locales: locales,
    isLoading: state.isLoading,
    isSaving: state.isSaving,
    onCancel: handleOnCancel,
    onSave: handleOnSave,
    data: state.data,
    originalData: state.originalData,
    className: "fields-validation__form",
    size: "large"
  }, /*#__PURE__*/React.createElement(ForceForm, {
    values: state.data,
    errors: state.errors,
    onChange: onChange,
    schema: schema.current,
    mode: "create",
    className: classes.root
  }), ((_state$data2 = state.data) === null || _state$data2 === void 0 ? void 0 : _state$data2.associatedFields) && !!Object.keys((_state$data3 = state.data) === null || _state$data3 === void 0 ? void 0 : _state$data3.associatedFields).length && !state.isDuplicate && /*#__PURE__*/React.createElement(Advice, {
    className: classes.adviceRoot,
    title: /*#__PURE__*/React.createElement(Text, {
      className: classes.adviceTitle,
      bold: true
    }, t('cfm_label_related_entities')),
    type: "info",
    showIcon: true
  }, Object.keys(state.data.associatedFields).map((key, index) => /*#__PURE__*/React.createElement("div", {
    key: key,
    className: classes.entity
  }, index > 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.divider
  }), /*#__PURE__*/React.createElement(Text, {
    className: classes.entityTitle,
    bold: true
  }, key), state.data.associatedFields[key].map(field => /*#__PURE__*/React.createElement(Text, {
    key: field,
    className: classes.field
  }, t(field)))))));
};
process.env.NODE_ENV !== "production" ? FormFieldsValidations.propTypes = {
  onRef: PropTypes.func.isRequired,
  refreshList: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  setActiveRowId: PropTypes.func,
  stackIndex: PropTypes.number
} : void 0;
export default /*#__PURE__*/memo(FormFieldsValidations);